import { useState, useEffect, useContext, useCallback } from 'react';
import { useEventListener } from '../../../infrastructure/events/useBroadcastChannel';
import {
  ChangeNotationRequestedEvent
} from '../../../infrastructure/events/channels';
import { LineProcessor } from '../services/line-processor';
import { JsonToSong } from '../services/json-to-song';
import { SongsExpander } from '../services/songs-expander';
import { AppCtx } from '../../../AppCtx';
import useYjsDocument from '../../../shared/hooks/useYjsDocument';
import { getWorkspaceId } from '../../../shared/services/dbUtils';
import useUserSettings from '../../../shared/hooks/useUserSettings';
import { ChordInlineBindingService } from '../services/chord-inline-binding';
import {cachedSongContent} from '../services/song-content-cache';

/**
 * A hook that centralizes song content processing and event handling
 * @param {boolean} expandSections - Whether to expand song sections
 * @returns {Object} - The processed songs json and related methods
 */
export function useSongContentService(expandSections = null, fileOverride, notationOverride, presentationSettings) {
  // Get context from AppCtx - we'll only use what we need
  const {
    selectedFile: localSelectedFile,
    isSongExpanded
  } = useContext(AppCtx);
  const selectedFile = fileOverride === undefined ? localSelectedFile : fileOverride;

  // Maintain our own state for songsJson
  const [songsJson, setSongsJson] = useState(null);
  const [notationOverrides, setNotationOverrides] = useState({});

  // Use expandSections parameter if provided, otherwise use isSongExpanded from context
  const shouldExpandSections = expandSections !== null ? expandSections : isSongExpanded;

  // Get content from Y.js document
  const { content } = useYjsDocument(getWorkspaceId(), selectedFile?.name, {contentOnly: true});

  // Get user settings
  const { settings } = useUserSettings();
  const preferredNotation = notationOverride ?? settings.chordMode;
  const transposeMode = presentationSettings?.presentationTransposeMode;
  const freezeKey = presentationSettings?.presentationFreezeKey;
  const tuningSemitones = presentationSettings?.presentationTuningSemitones;
  const hasPresentationSettings = !!presentationSettings;

  // Process content when it changes
  const processContent = useCallback((fileContent) => {
    // Handle empty content - set empty songs array
    if (!fileContent || fileContent.trim() === '') {
      setSongsJson({ songs: [] });
      return;
    }

    const key = JSON.stringify([process.env.REACT_APP_HOSANNA_DIRECT === '1', fileContent, selectedFile?.name, shouldExpandSections, preferredNotation,
      notationOverrides[selectedFile?.name], hasPresentationSettings, transposeMode, freezeKey, tuningSemitones]);
    const result = cachedSongContent(key, () => {
      const jsonToSong = new JsonToSong();
      const chordInlineBindingService = new ChordInlineBindingService();
      const linesProcessed = new LineProcessor().parseChordsTxt(fileContent, process.env.REACT_APP_HOSANNA_DIRECT === '1');
      let songsEnriched = jsonToSong.songsEnriched(linesProcessed);
      let songsTransposed = jsonToSong.songsTransposed(songsEnriched);
      if (hasPresentationSettings) {
        songsTransposed.forEach(song => {
          if (transposeMode === 'freeze-key' && freezeKey && song.keys?.length) jsonToSong.transposeToKey(song, freezeKey);
          else if (Number.isFinite(tuningSemitones) && tuningSemitones !== 0) jsonToSong.transpose(song, -Math.trunc(tuningSemitones));
        });
      }
      let songsWithNotation = jsonToSong.songsWithNotation(songsTransposed, preferredNotation);
      // Convert source chord lines before producing inline lyrics and section views.
      // Keep the local choice when a transpose causes the source document to update.
      songsWithNotation.forEach((song, index) => {
        const notation = notationOverrides[selectedFile?.name]?.[index];
        if (notation && notation !== song.notation) jsonToSong.changeNotation(song, notation);
      });

      // Convert songs to inline chord format for proper text wrapping
      let songsWithInlineChords = chordInlineBindingService.convertSongs(songsWithNotation);

      let processedSongsJson = { songs: songsWithInlineChords };

      // Optionally expand song sections
      if (shouldExpandSections) {
        processedSongsJson = new SongsExpander(processedSongsJson).expand();
      }

      // Sort songs by position
      processedSongsJson.songs = sortSongs(processedSongsJson.songs);

      return processedSongsJson;
    });
    setSongsJson(result);
  }, [shouldExpandSections, preferredNotation, notationOverrides, selectedFile?.name, transposeMode, freezeKey, tuningSemitones, hasPresentationSettings]);

  // Sort songs by position
  const sortSongs = useCallback((songs) => {
    return songs.sort((a, b) => a.currentPosition - b.currentPosition);
  }, []);

  // Handle notation change events
  useEventListener(ChangeNotationRequestedEvent, (message) => {
    if (message.fileName !== selectedFile?.name || !songsJson?.songs[message.index] ||
        !['polish', 'international'].includes(message.notation)) return;
    setNotationOverrides(previous => ({...previous,
      [message.fileName]: {...previous[message.fileName], [message.index]: message.notation}
    }));
  });

  // Process content from Y.js document when it changes
  useEffect(() => {
    setSongsJson(null);
  }, [selectedFile?.name]);

  useEffect(() => {
    if (content !== null && content !== undefined && selectedFile?.name) {
      processContent(content);
    }
  }, [content, selectedFile?.name, processContent]);

  return {
    songsJson,
    processContent
  };
}
